import {
  Box,
  VStack,
  Text,
  Heading,
  useColorModeValue,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MeetingSummary } from '../../types';

interface SectionNotesDisplayProps {
  summary: MeetingSummary;
}

/**
 * Section Notes Display Component
 * Displays section notes with proper formatting, handling both old and new formats
 */
export function SectionNotesDisplay({ summary }: SectionNotesDisplayProps) {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const headingColor = useColorModeValue('gray.800', 'gray.200');

  const renderGeneralNotes = (notes: Array<{ content: string }> | undefined) => {
    if (!notes || notes.length === 0) return null;

    return (
      <VStack align="stretch" spacing={2}>
        {notes.map((note, idx) => (
          <Box
            key={`general-${idx}`}
            bg={bgColor}
            borderRadius="md"
            p={3}
            borderWidth="1px"
            borderColor={borderColor}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ node, ...props }) => <Text color={textColor} fontSize="sm" mb={2} {...props} />,
                strong: ({ node, ...props }) => <Text as="strong" fontWeight="bold" color={textColor} {...props} />,
                em: ({ node, ...props }) => <Text as="em" fontStyle="italic" color={textColor} {...props} />,
                ul: ({ node, children, ...props }) => (
                  <Box as="ul" pl={4} mb={2} color={textColor} style={{ listStyleType: 'disc' }} {...(props as any)}>
                    {children}
                  </Box>
                ),
                ol: ({ node, children, ...props }) => (
                  <Box as="ol" pl={4} mb={2} color={textColor} style={{ listStyleType: 'decimal' }} {...(props as any)}>
                    {children}
                  </Box>
                ),
                li: ({ node, ...props }) => <Box as="li" mb={1} color={textColor} fontSize="sm" {...(props as any)} />,
                h1: ({ node, ...props }) => <Heading size="md" color={textColor} mb={2} {...props} />,
                h2: ({ node, ...props }) => <Heading size="sm" color={textColor} mb={2} {...props} />,
                h3: ({ node, ...props }) => <Heading size="xs" color={textColor} mb={2} {...props} />,
                code: ({ node, ...props }) => <Text as="code" bg="gray.100" px={1} py={0.5} borderRadius="sm" fontSize="xs" fontFamily="mono" color={textColor} {...props} />,
                pre: ({ node, ...props }) => <Box as="pre" bg="gray.100" p={2} borderRadius="sm" overflowX="auto" mb={2} {...(props as any)} />,
              }}
            >
              {note.content}
            </ReactMarkdown>
          </Box>
        ))}
      </VStack>
    );
  };

  const renderSection = (
    sectionName: string,
    sectionData:
      | {
          general?: Array<{ content: string }>;
          department_communications?: Array<{
            content: string;
            departments?: string[];
            assigned_users?: string[];
          }>;
        }
      | undefined
  ) => {
    if (!sectionData) return null;

    const hasGeneral = sectionData.general && sectionData.general.length > 0;
    const hasDeptComm =
      sectionData.department_communications && sectionData.department_communications.length > 0;

    if (!hasGeneral && !hasDeptComm) return null;

    return (
      <Box mb={6}>
        <Heading size="sm" color={headingColor} mb={3}>
          {sectionName}
        </Heading>
        {hasGeneral && renderGeneralNotes(sectionData.general)}
        {/* Department communications are handled separately by DepartmentCommunications component */}
      </Box>
    );
  };

  return (
    <VStack align="stretch" spacing={4}>
      {summary.presenterNotes &&
        Array.isArray(summary.presenterNotes) &&
        summary.presenterNotes.length > 0 && (
          <Box>
            <Heading size="sm" color={headingColor} mb={3}>
              Presenter Notes
            </Heading>
            <VStack align="stretch" spacing={2}>
              {summary.presenterNotes.map((note, idx) => (
                <Box
                  key={idx}
                  bg={bgColor}
                  borderRadius="md"
                  p={3}
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ node, ...props }) => <Text color={textColor} fontSize="sm" mb={2} {...props} />,
                      strong: ({ node, ...props }) => <Text as="strong" fontWeight="bold" color={textColor} {...props} />,
                      em: ({ node, ...props }) => <Text as="em" fontStyle="italic" color={textColor} {...props} />,
                      ul: ({ node, children, ...props }) => (
                        <Box as="ul" pl={4} mb={2} color={textColor} style={{ listStyleType: 'disc' }} {...(props as any)}>
                          {children}
                        </Box>
                      ),
                      ol: ({ node, children, ...props }) => (
                        <Box as="ol" pl={4} mb={2} color={textColor} style={{ listStyleType: 'decimal' }} {...(props as any)}>
                          {children}
                        </Box>
                      ),
                      li: ({ node, ...props }) => <Box as="li" mb={1} color={textColor} fontSize="sm" {...(props as any)} />,
                      h1: ({ node, ...props }) => <Heading size="md" color={textColor} mb={2} {...props} />,
                      h2: ({ node, ...props }) => <Heading size="sm" color={textColor} mb={2} {...props} />,
                      h3: ({ node, ...props }) => <Heading size="xs" color={textColor} mb={2} {...props} />,
                      code: ({ node, ...props }) => <Text as="code" bg="gray.100" px={1} py={0.5} borderRadius="sm" fontSize="xs" fontFamily="mono" color={textColor} {...props} />,
                      pre: ({ node, ...props }) => <Box as="pre" bg="gray.100" p={2} borderRadius="sm" overflowX="auto" mb={2} {...(props as any)} />,
                    }}
                  >
                    {note.content}
                  </ReactMarkdown>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

      {renderSection('Segue Notes', summary.segueNotes)}
      {renderSection('Scorecard Notes', summary.scorecardNotes)}
      {renderSection('Rock Review Notes', summary.rockReviewNotes)}
      {renderSection('Customer/Employee Headlines', summary.customerEmployeeHeadlines)}
      {renderSection('IDS Discussion Notes', summary.idsNotes)}
      {renderSection('Conclusion Notes', summary.concludeNotes)}
    </VStack>
  );
}

